import { Order } from "pages/admin/Orders"
import { Product } from "pages/admin/Products/Products"
import React from "react"
import {
  Card,
  CardBody,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from "reactstrap"
import { ProductActionMenu } from "./Components/ProductActionMenu"
import { useTranslation } from "react-i18next"

interface ProductCardProps {
  product: Product
  viewDetailsHandler: (product: Product) => void
  editProductHandler: (product: Product) => void
  deleteProductHandler: (product: Product) => void
  toggleActiveProduct: (product: Product) => void
  addNewOffer: (product: Product) => void
  addNewCampaign: (product: Product) => void
  duplicateProductHandler: (product: Product) => void
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  deleteProductHandler,
  editProductHandler,
  viewDetailsHandler,
  toggleActiveProduct,
  addNewCampaign,
  addNewOffer,
  duplicateProductHandler,
}) => {
  const { t } = useTranslation()

  return (
    <Col xxl="3" xl="4" sm="6">
      <Card className="text-center rounded-3 overflow-hidden shadow-md">
        <CardBody className="p-0">
          <div
            className="position-relative overflow-hidden"
            style={{ maxHeight: "200px" }}
          >
            <img
              src="https://placehold.co/30x30/png"
              className="card-img-top"
              alt="Product"
            />
            {product.featured ? (
              <span
                className="badge bg-success text-white fw-semibold position-absolute"
                style={{ top: "10px", right: "10px", fontSize: "0.8rem" }}
              >
                {t("products_featured")}
              </span>
            ) : (
              <></>
            )}
          </div>

          <div className="card-body p-2 px-3">
            <div className="d-flex justify-content-between align-items-start p-2">
              <div className="d-flex justify-content-between align-items-start w-100">
                <div className="text-start">
                  <h6 className="mb-0 fw-bold">{t("products_name")}</h6>
                  <small className="text-muted">{t("products_category")}</small>
                </div>
                <ProductActionMenu
                  product={product}
                  viewDetailsHandler={viewDetailsHandler}
                  editProductHandler={editProductHandler}
                  deleteProductHandler={deleteProductHandler}
                  addNewOffer={addNewOffer}
                  addNewCampaign={addNewCampaign}
                  duplicateProductHandler={duplicateProductHandler}
                />
              </div>
            </div>

            <div className="p3 px-2">
              <div className="d-flex justify-content-between mt-3">
                <span className="fw-bold">
                  {t("products_stock")} : {product.stock}
                </span>
                <span className="fw-bold">
                  {t("common_size")} : {product.size}
                </span>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-1">
                <span className="fw-bold">{product.price}</span>
                {product.couponBased ? (
                  <span className="badge bg-primary-30 text-primary fw-semibold me-1 px-2 py-1 font-size-10">
                    {t("products_couponBased")}
                  </span>
                ) : (
                  <span className="badge bg-cyan-30 text-cyan fw-semibold me-1 px-2 py-1 font-size-10">
                    {t("products_oneTime")}
                  </span>
                )}
              </div>
            </div>
            <div className="form-check form-switch mt-3">
              <input
                type="checkbox"
                className="form-check-input"
                id={`customSwitch${product.id}`}
                checked={product.status === "Active"}
                onClick={() => toggleActiveProduct(product)}
              />
              <label
                className="form-check-label text-capitalize"
                htmlFor={`customSwitch${product.id}`}
              >
                {product.status}
              </label>
            </div>
          </div>
        </CardBody>
      </Card>
    </Col>
  )
}

interface ProductsGridProps {
  products: Product[]
  viewDetailsHandler: (product: Product) => void
  editProductHandler: (product: Product) => void
  deleteProductHandler: (product: Product) => void
  toggleActiveProduct: (product: Product) => void
  addNewOffer: (product: Product) => void
  addNewCampaign: (product: Product) => void
  duplicateProductHandler: (product: Product) => void
}

export const ProductsGrid: React.FC<ProductsGridProps> = ({
  products,
  deleteProductHandler,
  editProductHandler,
  viewDetailsHandler,
  toggleActiveProduct,
  addNewCampaign,
  addNewOffer,
  duplicateProductHandler,
}) => {
  return (
    <Row>
      {products.map(product => (
        <ProductCard
          key={product.id}
          product={product}
          editProductHandler={editProductHandler}
          deleteProductHandler={deleteProductHandler}
          viewDetailsHandler={viewDetailsHandler}
          toggleActiveProduct={toggleActiveProduct}
          addNewCampaign={addNewCampaign}
          addNewOffer={addNewOffer}
          duplicateProductHandler={duplicateProductHandler}
        />
      ))}
    </Row>
  )
}
